<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert, useTrack } from 'dashboard/composables';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';

const store = useStore();
const { t } = useI18n();

const [showDropdown, toggleDropdown] = useToggle(false);
const closeDropdown = () => toggleDropdown(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const currentPriority = computed(() => currentChat.value.priority || null);

// Same order and labels the Details sidebar priority select uses.
const PRIORITY_OPTIONS = [
  { value: CONVERSATION_PRIORITY.URGENT, labelKey: 'URGENT' },
  { value: CONVERSATION_PRIORITY.HIGH, labelKey: 'HIGH' },
  { value: CONVERSATION_PRIORITY.MEDIUM, labelKey: 'MEDIUM' },
  { value: CONVERSATION_PRIORITY.LOW, labelKey: 'LOW' },
  { value: null, labelKey: 'NONE' },
];

const menuItems = computed(() =>
  PRIORITY_OPTIONS.map(({ value, labelKey }) => ({
    label: t(`CONVERSATION.PRIORITY.OPTIONS.${labelKey}`),
    value,
    action: 'priority',
    isSelected: currentPriority.value === value,
  }))
);

const assignPriority = ({ value: priority, label }) => {
  closeDropdown();
  if (priority === currentPriority.value) return;

  const conversationId = currentChat.value.id;
  const oldValue = currentPriority.value;

  store.dispatch('setCurrentChatPriority', { priority, conversationId });
  store
    .dispatch('assignPriority', { conversationId, priority })
    .then(() => {
      useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
        oldValue,
        newValue: priority,
        from: 'Conversation Header',
      });
      useAlert(
        t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
          priority: label,
          conversationId,
        })
      );
    })
    .catch(() => {
      store.dispatch('setCurrentChatPriority', {
        priority: oldValue,
        conversationId,
      });
      useAlert(t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'));
    });
};
</script>

<template>
  <div class="relative flex items-center">
    <NextButton
      v-tooltip="t('CONVERSATION.PRIORITY.TITLE')"
      :aria-label="t('CONVERSATION.PRIORITY.TITLE')"
      data-testid="conversation-priority-button"
      ghost
      slate
      sm
      @click="toggleDropdown()"
    >
      <template #icon>
        <!-- The icon keeps its own tooltip for list rows; pointer events are
        off here so only the button tooltip fires. -->
        <CardPriorityIcon
          :priority="currentPriority"
          show-empty
          class="!size-4 pointer-events-none !text-n-slate-10"
        />
      </template>
    </NextButton>
    <DropdownMenu
      v-if="showDropdown"
      v-on-clickaway="closeDropdown"
      :menu-items="menuItems"
      class="top-full mt-0.5 start-0 xl:start-auto xl:end-0 min-w-[9.75rem]"
      @action="assignPriority"
    >
      <template #icon="{ item }">
        <CardPriorityIcon
          :priority="item.value"
          show-empty
          class="!size-4 shrink-0 pointer-events-none !text-n-slate-10"
        />
      </template>
      <template #trailing-icon="{ item }">
        <span
          v-if="item.isSelected"
          class="i-lucide-check size-3.5 shrink-0 text-n-slate-11 ltr:ml-auto rtl:mr-auto"
        />
      </template>
    </DropdownMenu>
  </div>
</template>
